'use client';

import { GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { DesktopNavigationWidth, GNBHeight, isPC } from 'constants/layout';
import { GlobalNavigationMenuText } from 'constants/navigation';
import { useAuthState } from 'contexts/AuthProvider';
import { ContentsPartnersLogo } from 'icons/ContentsPartnersLogo';
import { LogoIcon } from 'icons/LogoIcon';
import { useGetInboxNotiUnreadCountQuery } from 'queries/noti/useGetInboxNotiUnreadCountQuery';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'ui-components/common/Link';
import { InboxLinkButton, SearchLinkButton } from 'ui-components/navigations/GlobalButtons';
import { UserMenuToggleButton } from 'ui-components/navigations/userMenu/UserMenuToggleButton';

export const GlobalNavigationBar = ({
  className,
  selectedMenu,
  showPartnersLogo = false,
}: {
  className?: string;
  selectedMenu?: GlobalNavigationMenu;
  showPartnersLogo?: boolean;
}) => {
  const { user } = useAuthState();
  const { data: unreadCountData } = useGetInboxNotiUnreadCountQuery(!!user);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 모바일에서만 처리된다.
  const sectionTitle = selectedMenu ? GlobalNavigationMenuText[selectedMenu] : undefined;
  const showLogo = selectedMenu === GlobalNavigationMenu.Home || !sectionTitle;

  const gnbButtons = useMemo(() => {
    if (user) {
      return [
        <SearchLinkButton key="search" />,
        <InboxLinkButton key="inbox" unreadCount={unreadCountData?.unreadCount ?? 0} />,
      ];
    }
    return [<SearchLinkButton key="search" />];
  }, [unreadCountData?.unreadCount, user]);

  const renderUser = useCallback(() => {
    if (!isMounted) {
      return <></>;
    }

    return (
      <>
        <StyledRightButtons>{gnbButtons}</StyledRightButtons>
        <UserMenuToggleButton />
      </>
    );
  }, [gnbButtons, isMounted]);

  return (
    <StyledContainer className={className}>
      <StyledPcWrapper>
        <Link href="/" replace>
          <LogoIcon />
        </Link>
        {showPartnersLogo && (
          <PartnersLogoWrapper>
            <ContentsPartnersLogo size={61} color={Color.Primary500} />
          </PartnersLogoWrapper>
        )}
      </StyledPcWrapper>
      <StyledMobileWrapper>
        {showLogo ? (
          <>
            <Link href="/" replace>
              <LogoIcon />
            </Link>
            {showPartnersLogo && (
              <PartnersLogoWrapper>
                <ContentsPartnersLogo size={61} color={Color.Primary500} />
              </PartnersLogoWrapper>
            )}
          </>
        ) : (
          <StyledSectionTitle>{sectionTitle}</StyledSectionTitle>
        )}
      </StyledMobileWrapper>
      <StyledRightSection>{renderUser()}</StyledRightSection>
    </StyledContainer>
  );
};

const StyledPcWrapper = styled.div`
  display: none;

  @media (${isPC}) {
    display: flex;
  }
  > a,
  div {
    display: flex;
  }
`;

const StyledMobileWrapper = styled.div`
  display: flex;

  @media (${isPC}) {
    display: none;
  }
`;

const StyledContainer = styled.div`
  height: ${GNBHeight}px;
  max-width: ${DesktopNavigationWidth}px;
  padding: 0 16px;
  position: relative;
  display: flex;
  align-items: center;
  margin: auto;
`;

const StyledRightSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
  margin-left: auto;
`;

const StyledSectionTitle = styled.p`
  ${Typography.XXLarge};
  font-weight: ${FontWeight.Bold};
`;

const StyledRightButtons = styled.section`
  display: flex;
  align-items: center;
  margin-right: 16px;

  & > * + * {
    margin-left: 16px;
  }
`;

const PartnersLogoWrapper = styled.div`
  padding-left: 8px;
`;
